/* ==========================================================================
   About Us Image - Asian American Forward Website
   ========================================================================== */

import { preventModalScroll, allowModalScroll } from './utils/scroll-prevention.js';

import { setup3DCardHover, isGSAPLoaded } from './utils/gsap-animations.js';
import { gsap } from 'gsap';

let aboutImageContainer = null;

export function initializeAboutImage() {
  console.log('🖼️ Initializing About Us image...');
  
  aboutImageContainer = document.querySelector('.about-img-container');
  
  if (!aboutImageContainer) {
    console.warn('About image container not found');
    return;
  }
  
  // Set up click handler for expansion
  aboutImageContainer.addEventListener('click', () => toggleImageExpansion());

  console.log('✅ About Us image initialized');
}

/* ==========================================================================
   3D Hover Effects (GSAP)
   ========================================================================== */

function setup3DAboutImageHover(container) {
  if (!isGSAPLoaded()) {
    console.warn('GSAP not loaded, skipping 3D hover setup');
    return;
  }
  
  console.log('✨ Setting up 3D hover for About Us image');
  
  let hoverTimeout = null;
  
  container.addEventListener('mouseenter', () => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    
    // Dispatch event to pause other animations if needed
    window.dispatchEvent(new CustomEvent('pauseCardCycling'));
    
    // Enhanced hover effect (match hero cards)
    gsap.to(container, {
      rotationX: 0,
      rotationY: 0,
      z: 35, // Same lift as hero cards
      duration: 0.3,
      ease: "power2.out"
    });
  });
  
  container.addEventListener('mouseleave', () => {
    // Reset on mouse leave (match hero cards)
    gsap.to(container, {
      rotationX: 0,
      rotationY: 0,
      z: 0,
      duration: 0.4,
      ease: "power2.out"
    });
    
    // Resume other animations after delay (match hero cards)
    hoverTimeout = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('resumeCardCycling'));
    }, 1000);
  });
  
  container.addEventListener('mousemove', (e) => {
    const rect = container.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const mouseX = (e.clientX - centerX) / (rect.width / 2);
    const mouseY = (e.clientY - centerY) / (rect.height / 2);
    
    // Calculate rotation based on mouse position (match hero cards)
    const rotateX = mouseY * -5; // Max 5 degrees (same as hero cards)
    const rotateY = mouseX * 5; // Max 5 degrees (same as hero cards)
    
    // Apply 3D rotation (match hero cards)
    gsap.to(container, {
      rotationX: rotateX,
      rotationY: rotateY,
      z: 40, // Same lift as hero cards
      duration: 0.1,
      ease: "none"
    });
  });
}

/* ==========================================================================
   Fallback 3D Mouse Effect (Vanilla JS)
   ========================================================================== */

function setup3DMouseEffect(container) {
  console.log('🎨 Setting up fallback 3D mouse effect for About Us image');
  
  container.addEventListener('mouseenter', () => {
    container.style.transform = 'translateZ(35px)';
  });
  
  container.addEventListener('mouseleave', () => {
    container.style.transform = '';
  });
  
  container.addEventListener('mousemove', (e) => {
    const rect = container.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const mouseX = (e.clientX - centerX) / (rect.width / 2);
    const mouseY = (e.clientY - centerY) / (rect.height / 2);
    
    // Calculate rotation based on mouse position (match hero cards)
    const rotateX = mouseY * -5; // Max 5 degrees (same as hero cards)
    const rotateY = mouseX * 5; // Max 5 degrees (same as hero cards)
    
    // Apply transform (match hero cards)
    container.style.transform = `
      translateZ(40px) 
      rotateX(${rotateX}deg) 
      rotateY(${rotateY}deg)
    `;
  });
}

/* ==========================================================================
   Expand/Contract Functionality (Same as Hero Cards)
   ========================================================================== */

function toggleImageExpansion() {
  const modal = document.getElementById('card-modal');
  const modalImage = document.getElementById('modal-image');
  const aboutImg = aboutImageContainer.querySelector('.about-img');
  
  if (!modal || !modalImage || !aboutImg) {
    console.warn('Modal elements not found');
    return;
  }
  
  // Check if modal is already open
  const isModalOpen = modal.style.display === 'flex';
  
  if (!isModalOpen) {
    expandImage();
  }
  // Note: We don't need to handle closing here since the modal handles it
}

function expandImage() {
  const modal = document.getElementById('card-modal');
  const modalImage = document.getElementById('modal-image');
  const aboutImg = aboutImageContainer.querySelector('.about-img');
  
  if (!modal || !modalImage || !aboutImg) return;
  
  // Ensure this is only called for about images, not other cards
  if (!aboutImageContainer.classList.contains('about-img-container')) {
    console.log('🚫 Ignoring non-about image expansion attempt');
    return;
  }
  
  console.log('🔍 Expanding About Us image');
  
  // Set modal image source
  modalImage.src = aboutImg.src;
  modalImage.alt = aboutImg.alt;
  
  // Prevent scrolling
  preventModalScroll();
  
  // Show modal (same as hero cards)
  modal.style.display = 'flex';
  modal.setAttribute('aria-hidden', 'false');
  modalImage.focus({ preventScroll: true });
  
  // Set up close handler (same as hero cards)
  const closeModal = () => {
    modal.style.display = 'none';
    modal.setAttribute('aria-hidden', 'true');
    
    // Re-enable scrolling
    allowModalScroll();
    
    aboutImageContainer.focus({ preventScroll: true }); // Return focus to image container
    
    // Remove event listeners
    modal.removeEventListener('click', closeModal);
    document.removeEventListener('keydown', handleKeydown);
  };
  
  // Close modal when clicking anywhere on the modal (same as hero cards)
  modal.addEventListener('click', closeModal);
  
  // Close on Escape key (same as hero cards)
  const handleKeydown = (e) => {
    if (e.key === 'Escape') {
      closeModal();
      document.removeEventListener('keydown', handleKeydown);
    }
  };
  
  document.addEventListener('keydown', handleKeydown);
}

/* ==========================================================================
   Reduced Motion Support
   ========================================================================== */

function checkReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Initialize with reduced motion check
if (checkReducedMotion()) {
  console.log('🎯 Reduced motion detected - About Us image animations will be minimal');
} 